import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import {
  Bell,
  Ticket,
  Trophy,
  Gift,
  Handshake,
  Newspaper,
  Filter,
  CheckCircle2,
} from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import {
  CommonInput,
  CommonSelect,
  CommonButton,
} from "../../../../Components/ui";

const TIME_FILTER_OPTIONS = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "7days", label: "Last 7 Days" },
  { value: "30days", label: "Last 30 Days" },
];

const NOTIFICATION_SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
];

const CATEGORY_TABS = [
  { id: "all", label: "All Alerts", icon: Bell, color: colors.primary.main },
  { id: "lottery", label: "Lotteries", icon: Ticket, color: colors.amber[600] },
  { id: "winner", label: "Winners", icon: Trophy, color: colors.emerald[600] },
  { id: "giftcard", label: "Gift Cards", icon: Gift, color: colors.purple[600] },
  { id: "partner", label: "Creators & Orgs", icon: Handshake, color: colors.cyan[600] },
  { id: "platform", label: "Platform", icon: Newspaper, color: colors.slate[600] },
];

const NotificationFilters = ({
  activeTab = "all",
  onTabChange,
  searchQuery = "",
  onSearchChange,
  timeRange = "all",
  onTimeRangeChange,
  sortBy = "newest",
  onSortChange,
  unreadOnly = false,
  onUnreadOnlyToggle,
  countsByTab = {},
  hasActiveFilters = false,
  onResetFilters,
}) => {
  return (
    <Card
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        mb: 3.5,
      }}
    >
      <Stack spacing={2.5}>
        {/* Top: Category Navigation Tabs */}
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{
            overflowX: "auto",
            pb: { xs: 1, sm: 0 },
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          {CATEGORY_TABS.map((tab) => {
            const isSelected = String(activeTab) === String(tab.id);
            const TabIcon = tab.icon;
            const count = countsByTab[tab.id] !== undefined ? countsByTab[tab.id] : 0;

            return (
              <Box
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: borderRadiusPx.lg,
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s ease-in-out",
                  backgroundColor: isSelected ? colors.primary.main : colors.slate[50],
                  border: `1px solid ${isSelected ? colors.primary.main : colors.slate[200]}`,
                  boxShadow: isSelected ? "0 4px 12px rgba(37, 99, 235, 0.25)" : "none",
                  "&:hover": {
                    backgroundColor: isSelected ? colors.primary.dark : colors.slate[100],
                    borderColor: isSelected ? colors.primary.dark : colors.slate[300],
                  },
                }}
              >
                <TabIcon size={16} color={isSelected ? colors.neutral.white : tab.color} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isSelected ? 700 : 600,
                    fontSize: "0.875rem",
                    color: isSelected ? colors.neutral.white : colors.slate[700],
                  }}
                >
                  {tab.label}
                </Typography>
                <Box
                  sx={{
                    px: 0.85,
                    py: 0.15,
                    borderRadius: borderRadiusPx.full,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    backgroundColor: isSelected ? "rgba(255, 255, 255, 0.25)" : colors.slate[200],
                    color: isSelected ? colors.neutral.white : colors.slate[700],
                  }}
                >
                  {count}
                </Box>
              </Box>
            );
          })}
        </Stack>

        {/* Bottom Bar: Search, Time Filter, Sort & Unread Toggle */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          {/* Search Input */}
          <Box sx={{ width: { xs: "100%", md: "340px" } }}>
            <CommonInput
              fullWidth
              size="small"
              placeholder="Search by title, event, ID, or text..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.slate[400], fontSize: 20 }} />
                </InputAdornment>
              }
              endAdornment={
                searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => onSearchChange("")} sx={{ p: 0.25 }}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            />
          </Box>

          {/* Controls: Time Filter, Sort, Unread Toggle & Reset */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent={{ xs: "space-between", md: "flex-end" }}
            flexWrap="wrap"
            sx={{ gap: 1 }}
          >
            {/* Time Filter */}
            <Box sx={{ minWidth: { xs: "130px", sm: "145px" } }}>
              <CommonSelect
                size="small"
                value={timeRange}
                onChange={(e) => onTimeRangeChange(e.target.value)}
                options={TIME_FILTER_OPTIONS}
              />
            </Box>

            {/* Sort Dropdown */}
            <Box sx={{ minWidth: { xs: "130px", sm: "145px" } }}>
              <CommonSelect
                size="small"
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                options={NOTIFICATION_SORT_OPTIONS}
              />
            </Box>

            {/* Unread Only Toggle Button */}
            <Box
              onClick={onUnreadOnlyToggle}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 2,
                height: "40px",
                minHeight: "40px",
                boxSizing: "border-box",
                borderRadius: borderRadiusPx.xl,
                cursor: "pointer",
                userSelect: "none",
                fontSize: "14px",
                fontWeight: unreadOnly ? 700 : 500,
                backgroundColor: unreadOnly ? colors.blue[50] : colors.background.paper,
                color: unreadOnly ? colors.primary.main : colors.slate[700],
                border: `1px solid ${unreadOnly ? colors.primary.main : colors.neutral.border}`,
                boxShadow: customShadows.cardFlat,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: unreadOnly ? colors.blue[100] : colors.slate[50],
                  borderColor: unreadOnly ? colors.primary.dark : colors.neutral.borderHover,
                },
              }}
            >
              <CheckCircle2 size={16} color={unreadOnly ? colors.primary.main : colors.slate[400]} />
              <Typography variant="body2" sx={{ fontWeight: "inherit", color: "inherit", fontSize: "14px", lineHeight: 1 }}>
                Unread Only
              </Typography>
            </Box>

            {/* Clear All Filters Button */}
            {hasActiveFilters && (
              <CommonButton
                variant="clear"
                size="input"
                onClick={onResetFilters}
                startIcon={<Filter size={15} />}
                sx={{
                  height: "40px",
                  minHeight: "40px",
                }}
              >
                Reset
              </CommonButton>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};

NotificationFilters.propTypes = {
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  timeRange: PropTypes.string.isRequired,
  onTimeRangeChange: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  unreadOnly: PropTypes.bool.isRequired,
  onUnreadOnlyToggle: PropTypes.func.isRequired,
  countsByTab: PropTypes.object,
  hasActiveFilters: PropTypes.bool,
  onResetFilters: PropTypes.func.isRequired,
};

export default NotificationFilters;
